import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { X } from "lucide-react";

const GalleryPage = () => {
  React.useEffect(() => { document.title = "Project Gallery | New Leaf PMC LLC"; }, []);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10]" data-testid="gallery-page">
      {/* Header */}
      <section className="py-12 md:py-16 bg-[#15161C] border-b border-[#2A2B32]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Work</h1>
          <p className="text-[#A0AAB2] text-lg max-w-2xl mx-auto">
            Browse through our recent projects and see the quality craftsmanship we bring to every job.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF6B35]"></div>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-24 h-24 rounded-full bg-[#15161C] border border-[#2A2B32] flex items-center justify-center mx-auto mb-4">
                <svg className="w-12 h-12 text-[#A0AAB2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">No projects uploaded yet</h2>
              <p className="text-[#A0AAB2]">Check back soon to see our latest work!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((image, idx) => (
                <div
                  key={image.id || idx}
                  className="group bg-[#15161C] border border-[#2A2B32] rounded-xl overflow-hidden cursor-pointer hover:border-[#FF6B35]/50 transition-all"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${idx}`}
                >
                  {/* Image */}
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={image.image_data}
                      alt={image.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  
                  {/* Description */}
                  <div className="p-4">
                    <h3 className="text-white font-semibold mb-1">{image.title}</h3>
                    {image.description && (
                      <p className="text-[#A0AAB2] text-sm line-clamp-2">{image.description}</p>
                    )}
                    {image.category && (
                      <span className="inline-block mt-2 px-2 py-1 bg-[#FF6B35]/20 text-[#FF6B35] text-xs rounded-full">
                        {image.category}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox-close"
          >
            <X size={24} />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.image_data}
              alt={selectedImage.title}
              className="w-full max-h-[70vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center">
              <h3 className="text-white text-xl font-semibold">{selectedImage.title}</h3>
              {selectedImage.description && (
                <p className="text-[#A0AAB2] mt-2">{selectedImage.description}</p>
              )}
              {selectedImage.category && (
                <span className="inline-block mt-2 px-3 py-1 bg-[#FF6B35]/20 text-[#FF6B35] text-sm rounded-full">
                  {selectedImage.category}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
